import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Taoyuan International Airport, Terminal 2 (桃園國際機場): the Airport MRT lands you in the
 * arrivals hall on the left; check-in counters along the top; the security line in the middle;
 * the departure gate with the plane behind glass on the right; shops and toilets at the bottom.
 */
const W = 44, H = 24;
const b = new MapBuilder(W, H, ' ');
b.room(0, 0, W, H, 'm');
// A tactile strip guides from the MRT to the counters and the gate.
b.fill(4, 12, 36, 1, 'e');
// The security line splits the hall: a wall with a gap.
b.fill(22, 1, 1, 8, '#').fill(22, 13, 1, 10, '#');
// Big windows on the right wall look onto the apron.
b.fill(43, 2, 1, 6, 'J').fill(43, 14, 1, 6, 'J');
// Moving walkway strip to the gate.
b.fill(24, 5, 16, 1, 'e');

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const decor: Decor[] = [
  { kind: 'ap_sign', x: px(4), y: 1, w: px(8), wall: true, variant: 0 }, // 入境 Arrivals
  { kind: 'ap_sign', x: px(27), y: 1, w: px(8), wall: true, variant: 1 }, // 登機門 Gates
  { kind: 'departures', x: px(13), y: 1, w: px(6), wall: true },
  { kind: 'window', x: px(36), y: 0, w: px(4), wall: true },
  { kind: 'ap_plane', x: px(31), y: 4, w: px(10), wall: true },
  { kind: 'ap_counter', ...at(8, 4), w: px(8), solid: { x: px(4) + 4, y: px(3) + 6, w: px(8) - 8, h: 10 } },
  { kind: 'ap_belt', ...at(5, 19), w: px(8), solid: { x: px(1) + 4, y: px(18), w: px(8) - 8, h: 12 } },
  { kind: 'stationBench', ...at(8, 10), w: px(3), solid: { x: px(6) + 10, y: px(10) - 6, w: px(3) - 4, h: 6 } },
  { kind: 'stationBench', ...at(14, 10), w: px(3), solid: { x: px(12) + 10, y: px(10) - 6, w: px(3) - 4, h: 6 } },
  { kind: 'stationBench', ...at(28, 10), w: px(3), solid: { x: px(26) + 10, y: px(10) - 6, w: px(3) - 4, h: 6 } },
  { kind: 'stationBench', ...at(34, 10), w: px(3), solid: { x: px(32) + 10, y: px(10) - 6, w: px(3) - 4, h: 6 } },
  { kind: 'stationBench', ...at(34, 16), w: px(3), solid: { x: px(32) + 10, y: px(16) - 6, w: px(3) - 4, h: 6 } },
  { kind: 'ticketGates', ...at(22, 11), w: px(3) },
  { kind: 'pottedPlant', x: px(2) + 8, y: px(2) + 12, solid: { x: px(2) + 2, y: px(2) + 6, w: 12, h: 6 } },
  { kind: 'pottedPlant', x: px(20) + 8, y: px(2) + 12, solid: { x: px(20) + 2, y: px(2) + 6, w: 12, h: 6 } },
  { kind: 'pottedPlant', x: px(24) + 8, y: px(21) + 12, solid: { x: px(24) + 2, y: px(21) + 6, w: 12, h: 6 } },
  { kind: 'pottedPlant', x: px(41) + 8, y: px(21) + 12, solid: { x: px(41) + 2, y: px(21) + 6, w: 12, h: 6 } },
  { kind: 'xy_pillarAd', x: px(18) + 8, y: px(16), variant: 0, solid: { x: px(18) + 3, y: px(16) - 4, w: 10, h: 4 } },
  { kind: 'xy_pillarAd', x: px(30) + 8, y: px(20), variant: 1, solid: { x: px(30) + 3, y: px(20) - 4, w: 10, h: 4 } },
  { kind: 'ap_trolleys', ...at(3, 15), solid: { x: px(2) + 2, y: px(15) - 8, w: 22, h: 8 } },
  { kind: 'escalator', ...at(2, 19), solid: { x: px(1), y: px(16), w: px(3), h: px(3) } },
];

const lights: Light[] = [
  { x: px(8), y: px(6), r: 110, color: '240,248,255' },
  { x: px(16), y: px(14), r: 110, color: '240,248,255' },
  { x: px(30), y: px(6), r: 110, color: '255,244,220' },
  { x: px(36), y: px(16), r: 110, color: '240,248,255' },
];

export const airport: MapDef = {
  id: 'airport',
  w: W,
  h: H,
  rows: b.rows(),
  indoor: true,
  theme: { wallFace: '#eef1f4', wallLight: '#f8fafc', wainscot: '#4a6f8a', wallTop: '#2a3440' },
  doors: [],
  decor,
  areas: [
    { key: 'ap_arrivals', rect: { x: 0, y: px(13), w: px(22), h: px(11) } },
    { key: 'ap_terminal', rect: { x: 0, y: 0, w: px(22), h: px(13) } },
    { key: 'ap_gates', rect: { x: px(22), y: 0, w: px(22), h: px(24) } },
  ],
  lights,
  spots: {
    /** The Airport MRT escalator brings you up into the arrivals hall. */
    mrt: { x: px(5), y: px(20) + 8, facing: 'right' },
    /** Landing from Barcelona: you walk out of the gate. */
    gate: { x: px(37), y: px(8) + 8, facing: 'down' },
  },
};
